import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Parse a DateOnly field from the server correctly
 * DateOnly fields are returned as UTC dates but should be treated as local dates
 * @param dateString - The date string from the server (e.g., "2025-06-02T00:00:00+00:00")
 * @returns Date object representing the local date, or null if invalid
 */
export function parseDateOnly(dateString: string | null | undefined): Date | null {
  if (!dateString) return null
  
  // Extract just the date part (YYYY-MM-DD) from the string
  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return null
  
  const [, year, month, day] = match
  // Create a new Date object with local timezone (month is 0-indexed in JS)
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
}

/**
 * Extracts the date part (YYYY-MM-DD) and appends T00:00:00
 * Browsers parse the result as local timezone
 */
export function normalizeUTCDateOnlyToLocal(dateString: string | null | undefined): string | null {
  if (!dateString) return null
  const datePart = dateString.split('T')[0]
  return `${datePart}T00:00:00`
}

/**
 * Returns the later of two nullable dates (or whichever one is non-null).
 * Used wherever a date has to respect a floor: defaulting a worker's First Day of Work to the
 * engagement start date when the worker's own start date is earlier (GW-97, GW-334).
 */
export function laterOf(a: Date | null, b: Date | null): Date | null {
  if (!a) return b
  if (!b) return a
  return a.getTime() >= b.getTime() ? a : b
}
